if (!customElements.get('product-description-read-more')) {
  class ProductDescriptionReadMore extends HTMLElement {
    static PARAGRAPH_LIMIT = 2;
    static SPECS_HEIGHT_BUFFER = 16;
    static HEIGHT_TOLERANCE = 8;

    connectedCallback() {
      this.bodies = this.querySelectorAll('.pdp-v2-info-spec__body-copy, .pdp-v2-info-spec__body--single');
      this.scheduleSetup();
    }

    scheduleSetup() {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.bodies.forEach((body) => this.setupBody(body));
        });
      });
    }

    getParagraphs(body) {
      const directParagraphs = Array.from(body.querySelectorAll(':scope > p'));
      if (directParagraphs.length > 0) return directParagraphs;

      return Array.from(body.querySelectorAll('p'));
    }

    getSpecsTargetHeight() {
      if (!window.matchMedia('(min-width: 769px)').matches) return null;

      const specsTable = this.closest('.pdp-v2-info-spec')?.querySelector('.spec-kv-table');
      if (!specsTable) return null;

      const specsHeight = specsTable.getBoundingClientRect().height;
      if (!specsHeight) return null;

      return Math.ceil(specsHeight - ProductDescriptionReadMore.SPECS_HEIGHT_BUFFER);
    }

    getParagraphHeight(paragraphs, limit = ProductDescriptionReadMore.PARAGRAPH_LIMIT) {
      if (!paragraphs.length) return 0;

      const body = paragraphs[0].parentElement;
      const bodyStyles = window.getComputedStyle(body);
      const gap = Number.parseFloat(bodyStyles.rowGap || bodyStyles.gap || '0') || 0;
      const count = Math.min(limit, paragraphs.length);

      let height = 0;

      for (let i = 0; i < count; i += 1) {
        height += paragraphs[i].getBoundingClientRect().height;
        if (i < count - 1) height += gap;
      }

      return Math.ceil(height);
    }

    getCollapsedHeight(body, paragraphs) {
      const specsTargetHeight = this.getSpecsTargetHeight();
      const contentHeight = body.scrollHeight;

      if (specsTargetHeight) {
        return Math.min(specsTargetHeight, contentHeight);
      }

      return Math.min(this.getParagraphHeight(paragraphs), contentHeight);
    }

    setupBody(body) {
      if (body.dataset.readMoreReady === 'true') return;

      const paragraphs = this.getParagraphs(body);
      const collapsedHeight = this.getCollapsedHeight(body, paragraphs);

      if (!collapsedHeight || contentHeightWithinTolerance(body, collapsedHeight)) return;

      body.dataset.readMoreReady = 'true';
      body.classList.add('pdp-v2-info-spec__body--collapsed');
      body.style.setProperty('--pdp-v2-description-collapsed-height', `${collapsedHeight}px`);

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'pdp-v2-info-spec__read-toggle link text-sm';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = this.dataset.readMoreLabel || 'Read more';

      toggle.addEventListener('click', () => {
        const isExpanded = body.classList.toggle('pdp-v2-info-spec__body--expanded');
        toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        toggle.textContent = isExpanded
          ? (this.dataset.readLessLabel || 'Read less')
          : (this.dataset.readMoreLabel || 'Read more');
      });

      body.insertAdjacentElement('afterend', toggle);
    }
  }

  function contentHeightWithinTolerance(body, collapsedHeight) {
    return body.scrollHeight <= collapsedHeight + ProductDescriptionReadMore.HEIGHT_TOLERANCE;
  }

  customElements.define('product-description-read-more', ProductDescriptionReadMore);
}
